import React, { useState, useEffect, useContext } from "react";
import { DateHelper, emptyObjectId, SwedenCalendar } from "../../../../common/src";
import { gql, useQuery } from "urql";
import { useParams, useLocation, useSearchParams } from "wouter";
import { Dropdown } from "../../../../components/src/Dropdown";

import { MiniTransactionForm } from "../MiniTransactionForm";
import { DateForm } from "../../../../components/src/dateForm";
import { CollateralGrid } from "./CollateralGrid";
import { InstrumentProductTypeEnum, PartyType, PermissionAssetEnum, TransactionType } from "../../types.generated";
import { cloneDeep, keyBy, sortBy } from "lodash";
import { ClientContextSelector } from "../../contexts/ClientContextSelector";
import { ClientContext } from "../../../../components/src/contexts/ClientContext";
import { Panel } from "../../../../components/src/common/Panel";
import { GET_ME } from "../../common/queries";

const getReconcileExposureCollateral = gql`
    query reconcileExposureCollateral($clientIds: [GraphQLObjectId!]!, $date: GraphQLDateString!) {
        reconcileExposureCollateral(clientIds: $clientIds, date: $date) {
            externalAccountId
            externalAccount {
                name
            }
            agreementType
            clientId
            client {
                name
            }
            instrumentId
            instrument {
                isin
                name
            }
            exposure
            variationMarginPosted
            variationMarginCollateral
            variationMarginSecuritiesCollateral
            netExposure
            quantity
            quantityDifference
            counterpartyNetExposure
            currency
            baseCurrency
            exposureDifference
            variationMarginCollateralDifference
            variationMarginSecuritiesCollateralDifference
            netExposureDifference
            counterpartyExposure
            counterpartyQuantity
            counterpartyVariationMarginPosted
            counterpartyVariationMarginCollateral
            counterpartyVariationMarginSecuritiesCollateral
            minimumTransferAmount
            roundingAmount
            threshold
            marginCall
            counterpartyMarginCall
            haircut
            counterpartyHaircut
            haircutDifference
            valuationDate
            counterpartyValuationDate
            initialMarginRequirement
            initialMarginCollateral
            initialMarginSecuritiesCollateral
            counterpartyInitialMarginCollateral
            counterpartyInitialMarginSecuritiesCollateral
            initialMarginCollateralDifference
            initialMarginSecuritiesCollateralDifference
            initialMarginNetRequirement
            counterpartyInitialMarginNetRequirement
            counterpartyInitialMarginPosted
            netInitialMarginRequirementDifference
            marginCallsMade
            interestRateYieldDeltaSum
            creditYieldDeltaSum
            reconcileType
            type
            marginType
            missingReport
        }
    }
`;

const getParties = gql`
    query {
        funds: parties(filter: { typeIn: [Fund, Mandate], statusIn: [Confirmed] }) {
            _id
            name
            instruments {
                _id
                productType
            }
        }
    }
`;

export type ClientInfo = {
    _id: string;
    label: string;
    value: string;
};

export const CollateralPage = (): React.ReactElement => {
    const { client } = useContext(ClientContext);
    const [queryArgs, setQueryArgs] = useSearchParams();
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [partiesById, setPartiesById] = useState({});
    const [collateralInstrumentIdsByClientId, setCollateralInstrumentIdsByClientId] = useState({});

    const [, navigate] = useLocation();

    const [{ data: dataParties, fetching: loadingParties, error: errorParties }] = useQuery({ query: getParties });
    const [{ data: dataReconciled, fetching: loadingReconciled, error: errorReconciled }, refetch] = useQuery({
        query: getReconcileExposureCollateral,
        variables: { clientIds: Object.keys(partiesById), date: queryArgs.get("endDate") },
        pause: !Object.keys(partiesById).length || !queryArgs.get("endDate")
    });

    const [{ fetching: loadingMe, error: errorMe, data: data }] = useQuery({
        query: GET_ME,
        requestPolicy: "network-only"
    });

    const params = useParams<{ id: string }>();
    let id = params.id;

    const marginCallTypes = [TransactionType.Insert, TransactionType.Transfer];

    let type = marginCallTypes[0];

    if (id && id.startsWith("new")) {
        type = marginCallTypes.find((d) => d.toLowerCase() === id.substring(3, id.length));
        id = "new";
    }

    useEffect(() => {
        // Adjusting to prev business date if needed
        const swedenCalendar = new SwedenCalendar();
        const date = queryArgs.get("endDate") || new Date().toISOString().slice(0, 10);
        let previousDate = DateHelper.dateAddDays(new Date(date), -1);
        const todaysDate = DateHelper.dateToString(DateHelper.today()).substring(0, 10);
        const yesterday = DateHelper.dateAddDays(new Date(todaysDate), -1);
        let endDate = cloneDeep(date);

        if (!Object.keys(partiesById).length && dataParties && !loadingParties && !errorParties) {
            const instrumentIdsByClientId: Record<string, string[]> = {};
            for (const fund of dataParties.funds) {
                instrumentIdsByClientId[fund._id] = [];
                for (const instrument of fund.instruments) {
                    if (
                        instrument.productType === InstrumentProductTypeEnum.VariationMarginAccount ||
                        instrument.productType === InstrumentProductTypeEnum.InitialMarginAccount
                    ) {
                        instrumentIdsByClientId[fund._id].push(instrument._id);
                    }
                }
            }
            setCollateralInstrumentIdsByClientId(instrumentIdsByClientId);
            setPartiesById(keyBy(sortBy(dataParties.funds, "name"), "_id"));
        }

        if (selectedClientId !== client._id) {
            setSelectedClientId(client._id);
        }

        if (new Date(date) >= new Date(todaysDate) || !swedenCalendar.isBusinessDay(date)) {
            while (!swedenCalendar.isBusinessDay(previousDate) || new Date(previousDate) > new Date(yesterday)) {
                previousDate = DateHelper.dateAddDays(previousDate, -1);
            }
            endDate = DateHelper.dateToString(previousDate).substring(0, 10);
        }

        if (queryArgs.size !== 0 || queryArgs.get("endDate") !== endDate || date !== endDate) {
            if (Object.keys(partiesById).length) {
                refetch({
                    variables: { date: endDate, clientIds: Object.keys(partiesById) }
                });
            }
            setQueryArgs((prev) => {
                prev.set("endDate", endDate);
                return prev;
            });
        }
    }, [client._id, dataParties, errorParties, loadingParties, partiesById, queryArgs, refetch, selectedClientId, setQueryArgs]);

    if (errorReconciled)
        return (
            <p>
                Error exposure collateral data:{" "}
                {errorReconciled.message ? errorReconciled.message : JSON.stringify(errorReconciled, null, 2)}
            </p>
        );
    if (errorParties) return <p>Error funds: {errorParties.message ? errorParties.message : JSON.stringify(errorParties, null, 2)}</p>;
    if (errorMe)
        return (
            <div>
                <p>Error:</p>
                <pre> {JSON.stringify(errorMe, null, 2)}</pre>
            </div>
        );

    if (loadingParties) return <p>Loading funds</p>;
    if (loadingReconciled) return <p>Loading exposure collateral data</p>;
    if (loadingMe) return <div>Loading...</div>;

    return (
        <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-4/12 px-2 mb-4">
                    <ClientContextSelector
                        typeIn={[PartyType.Fund, PartyType.Mandate]}
                        asset={PermissionAssetEnum.Collateral}
                        defaultClientName="Summary"
                    />
                </div>
                <div className="w-full md:w-8/12 px-2 mb-4 text-right">
                    {data &&
                    data.me &&
                    data.me.frontendRole &&
                    data.me.frontendRole.assets &&
                    (data.me.frontendRole.assets.includes(PermissionAssetEnum.BackOffice) || data.me.frontendRole.assets.length === 0) ? (
                        <a
                            href="https://github.com/CaptorAB/documents/blob/master/BackOffice/manual/collateral_management.md"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Manual
                        </a>
                    ) : null}
                </div>
            </div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-6/12 px-2 mb-4">
                    <DateForm
                        defaultDateString={new Date().toISOString().slice(0, 10)}
                        dateName={"endDate"}
                        label="End date"
                        className="width-fixed"
                    ></DateForm>
                </div>
                <div className="w-full md:w-6/12 px-2 mb-4 flex justify-end items-end">
                    <Dropdown align="end">
                        <Dropdown.Toggle variant="primary" size="sm">
                            New margin call
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {marginCallTypes.map((type, i) => (
                                <Dropdown.Item
                                    key={i}
                                    as="button"
                                    onClick={() =>
                                        navigate(
                                            `/reconcile/collateral/${client.dashName}/new${type.toLowerCase()}${queryArgs.toString() ? `?${queryArgs.toString()}` : ""}`
                                        )
                                    }
                                >
                                    {type}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>

            <div className="flex flex-wrap -mx-2">
                {dataReconciled &&
                dataReconciled.reconcileExposureCollateral &&
                dataReconciled.reconcileExposureCollateral.length &&
                queryArgs.toString() ? (
                    <div className="w-full px-2 mb-4">
                        <CollateralGrid
                            reconciledExposureCollateral={dataReconciled.reconcileExposureCollateral}
                            clientIds={selectedClientId !== emptyObjectId ? [selectedClientId] : Object.keys(partiesById)}
                            endDate={queryArgs.get("endDate") as string}
                            partiesById={partiesById}
                            collateralInstrumentIds={collateralInstrumentIdsByClientId}
                            clientDashName={selectedClientId !== emptyObjectId && client ? client.dashName : null}
                        />
                    </div>
                ) : null}
            </div>

            <Panel
                isOpen={id != null}
                onDismiss={() =>
                    navigate(`/reconcile/collateral/${client.dashName}${queryArgs.toString() ? `?${queryArgs.toString()}` : ""}`)
                }
            >
                <MiniTransactionForm id={id === "new" ? null : id} type={id === "new" ? type : null} showHeader />
            </Panel>
        </div>
    );
};
